import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Box, Typography, List, ListItem, ListItemText, IconButton, Button,
  CircularProgress, Alert, Divider, Paper, Stack, Autocomplete, TextField, Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useApi } from '../../context/ApiContext';
import { useResources } from '../../context/ResourceContext';
import { getContentTypeConfig } from '../content/ContentTypeRegistry';
import ResourceTypeIcon from '../resources/ResourceTypeIcon';
import DeleteDialog from '../common/DeleteDialog';
import { useConfig } from '../../context/ConfigContext';
import { createDate as createDateApi } from '../../services/dateApi';
import DomainObjectModal from './DomainObjectModal';

export const ReferenceActions = {
  CREATE: 'CREATE',
  EDIT: 'EDIT',
  DELETE: 'DELETE',
  VIEW_ONLY: 'VIEW_ONLY',
  ALL: 'ALL',
};

const DefaultItemViewer = ({ resource, onActionSuccess }) => {
  const { domainConfigs } = useConfig();
  const [modalConfig, setModalConfig] = useState({ open: false, resource: null, config: null });

  if (!resource) return <Typography variant="body2" color="text.secondary">Loading...</Typography>;

  const resourceType = resource.type || resource.resource_type;
  const config = domainConfigs?.[resourceType];
  const { getTitle } = getContentTypeConfig(resourceType);
  const title = config?.getDisplayName ? config.getDisplayName(resource) : getTitle(resource);
  const IconComponent = config?.icon ? (typeof config.icon === 'function' ? config.icon(resource) : config.icon) : null;
  const isClickable = !!config;

  const handleOpen = () => {
    if (!resource || !isClickable) return;
    setModalConfig({ open: true, resource: resource, config: config });
  };

  const handleCloseModal = () => {
    setModalConfig({ open: false, resource: null, config: null });
  };

  const handleModalActionSuccess = (action, item) => {
    if (action === 'delete') {
      handleCloseModal();
      if (onActionSuccess) {
        onActionSuccess(action, item);
      }
    }
  };

  return (
    <>
      <Tooltip title={isClickable ? `View details for: ${title}` : title}>
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{
            overflow: 'hidden',
            cursor: isClickable ? 'pointer' : 'default',
            '&:hover': isClickable ? {
              textDecoration: 'underline',
              color: 'primary.main',
            } : {},
          }}
          onClick={handleOpen}
        >
          {IconComponent ? <IconComponent fontSize="small" /> : <ResourceTypeIcon type={resource.type} />}
          <Typography variant="body2" noWrap>
            {title}
          </Typography>
        </Stack>
      </Tooltip>
      {modalConfig.open && modalConfig.config && (
        <DomainObjectModal
          open={modalConfig.open}
          onClose={handleCloseModal}
          itemToEdit={modalConfig.resource}
          config={modalConfig.config}
          sectionTitle={modalConfig.config.singularName}
          onActionSuccess={handleModalActionSuccess}
        />
      )}
    </>
  );
};

const AddControl = ({ childResourceType, parentId, parentType, parentObject, creationConfig, onAddSuccess, onCancel, existingReferences = [] }) => {
  const { reference: referenceApi } = useApi();
  const { domainConfigs } = useConfig();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [options, setOptions] = useState([]);
  const [selectedValue, setSelectedValue] = useState(null);
  const [dateValue, setDateValue] = useState(new Date());

  const resourceType = childResourceType[0];

  useEffect(() => {
    if (resourceType !== 'date') {
      const config = domainConfigs?.[resourceType];
      if (config?.api?.getAll) {
        setIsLoading(true);
        config.api.getAll()
          .then(data => {
            const existingChildIds = new Set((existingReferences || []).map(ref => ref.child_id));
            const availableOptions = (data || []).filter(option => !existingChildIds.has(option.id));
            setOptions(availableOptions);
          })
          .catch(err => setError(`Failed to load ${config.pluralName}: ${err.message}`))
          .finally(() => setIsLoading(false));
      }
    }
  }, [resourceType, domainConfigs, existingReferences]);

  const handleAdd = async () => {
    setIsLoading(true);
    setError(null);
    try {
      let childId;
      let childType = resourceType;
      let newResource = null;

      if (resourceType === 'date') {
        const rule = creationConfig?.date;
        const basePayload = {
          start_datetime: dateValue,
          end_datetime: dateValue,
          all_day: true,
        };
        const rulePayload = (rule && parentObject) ? rule(parentObject) : {};
        const finalPayload = {
          title: `Scheduled on ${dateValue.toLocaleDateString()}`,
          ...basePayload,
          ...rulePayload,
        };
        const dateResource = await createDateApi(finalPayload);
        childId = dateResource.id;
        newResource = dateResource;
      } else {
        if (!selectedValue) throw new Error("Please select an item to add.");
        childId = selectedValue.id;
        newResource = selectedValue;
      }

      const action = {
        action: 'add',
        parent_id: parentId,
        parent_type: parentType,
        child_id: childId,
        child_type: childType,
      };

      const response = await referenceApi.manageReferences([action]);
      if (!response.results[0].success) {
        throw new Error(response.results[0].message || 'Failed to create reference.');
      }

      onAddSuccess(newResource);
      setSelectedValue(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  if (resourceType === 'date') {
    return (
      <Stack spacing={1}>
        {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
        <Stack direction="row" spacing={1} alignItems="center">
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Select Date"
              value={dateValue}
              onChange={(newValue) => setDateValue(newValue)}
              slotProps={{ textField: { size: 'small' } }}
            />
          </LocalizationProvider>
          <Button onClick={handleAdd} disabled={isLoading} variant="contained" size="small">
            {isLoading ? <CircularProgress size={20} /> : 'Add Date'}
          </Button>
          <Button onClick={onCancel} color="inherit" size="small">
            Cancel
          </Button>
        </Stack>
      </Stack>
    );
  }

  const config = domainConfigs?.[resourceType];

  return (
    <Stack spacing={1}>
      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
      <Stack direction="row" spacing={1} alignItems="center">
        <Autocomplete
          fullWidth
          size="small"
          options={options}
          getOptionLabel={(option) => config?.getDisplayName(option) || option.name || option.title || ''}
          value={selectedValue}
          onChange={(event, newValue) => setSelectedValue(newValue)}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          renderInput={(params) => <TextField {...params} label={`Select ${config?.singularName || 'Item'}`} />}
          loading={isLoading}
        />
        <Button onClick={handleAdd} disabled={isLoading || !selectedValue} variant="contained" size="small">
          {isLoading ? <CircularProgress size={20} /> : 'Add'}
        </Button>
        <Button onClick={onCancel} color="inherit" size="small">
          Cancel
        </Button>
      </Stack>
    </Stack>
  );
};

const ReferenceManager = ({
  parentIds = [],
  childIds = [],
  referenceIds = [],
  parentResourceType = [],
  childResourceType = [],
  actions = ReferenceActions.ALL,
  parentViewer: ParentViewer = DefaultItemViewer,
  childViewer: ChildViewer = DefaultItemViewer,
  title,
  sx,
  hideKnownParent = false,
  hideKnownChild = false,
  creationConfig = {},
  maxReferences = null,
  directParentObject = null,
  onActionSuccess,
}) => {
  const { reference: referenceApi, resource: resourceApi } = useApi();
  const { getResourceById, refreshKey } = useResources();
  const [references, setReferences] = useState([]);
  const [objects, setObjects] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showAddUI, setShowAddUI] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  // FIX: Track if we have loaded data at least once for the current IDs
  const hasLoadedRef = useRef(false);
  const idsHash = useMemo(() => [...parentIds, ...childIds, ...referenceIds].sort().join(','), [parentIds, childIds, referenceIds]);

  // Reset loaded state when the target IDs change
  useEffect(() => {
    hasLoadedRef.current = false;
  }, [idsHash]);

  const canCreate = actions === ReferenceActions.ALL || actions === ReferenceActions.CREATE;
  const canDelete = actions === ReferenceActions.ALL || actions === ReferenceActions.DELETE;

  const isSingleParentMode = parentIds.length === 1 && childIds.length === 0;
  const isSingleChildMode = childIds.length === 1 && parentIds.length === 0;

  const canAddMore = useMemo(() => {
    if (maxReferences === null || maxReferences === undefined) {
      return true;
    }
    return references.length < maxReferences;
  }, [references.length, maxReferences]);

  const fetchData = useCallback(async () => {
    // FIX: Only show loading spinner if we haven't loaded data yet
    if (!hasLoadedRef.current) {
      setIsLoading(true);
    }
    setError(null);
    try {
      const queryParams = new URLSearchParams();
      parentIds.forEach(id => queryParams.append('parent_id', id));
      childIds.forEach(id => queryParams.append('child_id', id));
      referenceIds.forEach(id => queryParams.append('id', id));
      parentResourceType.forEach(type => queryParams.append('parent_type', type));
      childResourceType.forEach(type => queryParams.append('child_type', type));

      const fetchedReferences = await referenceApi.getReferences(queryParams);

      const idsToFetch = new Map();
      fetchedReferences.forEach(ref => {
        idsToFetch.set(ref.parent_id, ref.parent_type);
        idsToFetch.set(ref.child_id, ref.child_type);
      });

      if (isSingleParentMode && parentIds[0]) {
        idsToFetch.set(parentIds[0], parentResourceType[0]);
      }

      let newObjects = {};
      if (idsToFetch.size > 0) {
        const fetchPromises = Array.from(idsToFetch.entries()).map(async ([id, type]) => {
          const cachedResource = getResourceById(id);
          if (cachedResource) return { [id]: cachedResource };
          const fetched = await resourceApi.fetchResource(type, id);
          return { [id]: fetched };
        });
        const results = await Promise.all(fetchPromises);
        newObjects = results.reduce((acc, res) => ({ ...acc, ...res }), {});
      }

      // Auto-heal broken references (references pointing to non-existent objects)
      const brokenReferences = fetchedReferences.filter(ref =>
        (newObjects.hasOwnProperty(ref.parent_id) && newObjects[ref.parent_id] === null) ||
        (newObjects.hasOwnProperty(ref.child_id) && newObjects[ref.child_id] === null)
      );

      if (brokenReferences.length > 0) {
        console.warn(`[ReferenceManager] Auto-healing: Found ${brokenReferences.length} broken references. Deleting them now.`, brokenReferences);
        const deleteActions = brokenReferences.map(ref => ({
          action: 'remove',
          parent_id: ref.parent_id,
          parent_type: ref.parent_type,
          child_id: ref.child_id,
          child_type: ref.child_type,
        }));
        await referenceApi.manageReferences(deleteActions);
        
        const brokenRefIds = new Set(brokenReferences.map(r => r.id));
        const validReferences = fetchedReferences.filter(r => !brokenRefIds.has(r.id));
        setReferences(validReferences);
        setObjects(prev => ({ ...prev, ...newObjects }));
        
        if (onActionSuccess) {
          onActionSuccess('auto-heal-delete', { deleted: brokenReferences });
        }
      } else {
        setReferences(fetchedReferences);
        setObjects(prev => ({ ...prev, ...newObjects }));
      }
      
      // FIX: Mark as loaded so subsequent refreshes don't trigger the spinner
      hasLoadedRef.current = true;

    } catch (err) {
      console.error("Error in ReferenceManager:", err);
      setError(err.message || "Failed to load references.");
    } finally {
      setIsLoading(false);
    }
  }, [parentIds.join(','), childIds.join(','), referenceIds.join(','), parentResourceType.join(','), childResourceType.join(','), referenceApi, resourceApi, getResourceById, isSingleParentMode, onActionSuccess]);

  useEffect(() => {
    fetchData();
  }, [fetchData, refreshKey]);

  const handleDeleteReference = async () => {
    if (!itemToDelete) return;
    setIsLoading(true);
    setError(null);
    try {
      await referenceApi.deleteReferenceById(itemToDelete.id);
      const deletedRef = { ...itemToDelete };
      setItemToDelete(null);
      await fetchData();
      onActionSuccess?.('delete-reference', { reference: deletedRef });
    } catch (err) {
      setError(err.message || "Failed to delete reference.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddSuccess = (newlyCreatedResource) => {
    if (newlyCreatedResource) {
      setObjects(prev => ({ ...prev, [newlyCreatedResource.id]: newlyCreatedResource }));
    }
    setShowAddUI(false);
    fetchData();
    onActionSuccess?.('create-reference', { child: newlyCreatedResource, parent: finalParentObject });
  };

  const internalParentObject = isSingleParentMode ? objects[parentIds[0]] : null;
  const finalParentObject = directParentObject || internalParentObject;

  const renderList = () => (
    <List dense>
      {references.map(ref => {
        const parent = objects[ref.parent_id];
        const child = objects[ref.child_id];
        const showParent = !(hideKnownParent && isSingleParentMode);
        const showChild = !(hideKnownChild && isSingleChildMode);

        return (
          <ListItem
            key={ref.id}
            secondaryAction={canDelete ? (
              <IconButton edge="end" aria-label="delete" onClick={() => setItemToDelete(ref)}>
                <DeleteIcon />
              </IconButton>
            ) : null}
          >
            <ListItemText
              primary={
                <Stack direction="row" alignItems="center" spacing={1}>
                  {showParent && <Box sx={{ minWidth: 100, flex: 1 }}><ParentViewer resource={parent} onActionSuccess={onActionSuccess} /></Box>}
                  {showParent && showChild && <ArrowForwardIcon fontSize="small" color="action" />}
                  {showChild && <Box sx={{ minWidth: 100, flex: 1 }}><ChildViewer resource={child} onActionSuccess={onActionSuccess} /></Box>}
                </Stack>
              }
            />
          </ListItem>
        );
      })}
    </List>
  );

  const buttonVerb = maxReferences === 1 ? 'Set' : 'Add';

  return (
    <Paper variant="outlined" sx={{ p: 2, ...sx }}>
      {title && <Typography variant="h6" gutterBottom>{title}</Typography>}
      
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}><CircularProgress /></Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : (
        <>
          {references.length > 0 && renderList()}
          
          {references.length === 0 && !showAddUI && canCreate && isSingleParentMode && childResourceType.length > 0 && canAddMore && (
            <Box sx={{ p: 2, textAlign: 'center' }}>
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => setShowAddUI(true)} disabled={isLoading}>
                {buttonVerb} {childResourceType[0].replace(/_/g, ' ')}
              </Button>
            </Box>
          )}

          {canCreate && isSingleParentMode && childResourceType.length > 0 && canAddMore && (
            <>
              {references.length > 0 && <Divider sx={{ my: 1 }} />}
              <Box sx={{ mt: 2 }}>
                {showAddUI ? (
                  <AddControl
                    childResourceType={childResourceType}
                    parentId={parentIds[0]}
                    parentType={parentResourceType[0]}
                    parentObject={finalParentObject}
                    creationConfig={creationConfig}
                    onAddSuccess={handleAddSuccess}
                    onCancel={() => setShowAddUI(false)}
                    existingReferences={references}
                  />
                ) : (
                  references.length > 0 && (
                    <Button startIcon={<AddIcon />} onClick={() => setShowAddUI(true)} size="small" disabled={isLoading}>
                      {buttonVerb} {childResourceType[0].replace(/_/g, ' ')}
                    </Button>
                  )
                )}
              </Box>
            </>
          )}
        </>
      )}

      <DeleteDialog
        open={!!itemToDelete}
        onClose={() => setItemToDelete(null)}
        onConfirm={handleDeleteReference}
        itemName="this reference"
        itemType="reference"
        isDeleting={isLoading}
      />
    </Paper>
  );
};

ReferenceManager.propTypes = {
  parentIds: PropTypes.arrayOf(PropTypes.string),
  childIds: PropTypes.arrayOf(PropTypes.string),
  referenceIds: PropTypes.arrayOf(PropTypes.string),
  parentResourceType: PropTypes.arrayOf(PropTypes.string),
  childResourceType: PropTypes.arrayOf(PropTypes.string),
  actions: PropTypes.oneOf(Object.values(ReferenceActions)),
  parentViewer: PropTypes.elementType,
  childViewer: PropTypes.elementType,
  title: PropTypes.string,
  sx: PropTypes.object,
  hideKnownParent: PropTypes.bool,
  hideKnownChild: PropTypes.bool,
  maxReferences: PropTypes.number,
  creationConfig: PropTypes.object,
  directParentObject: PropTypes.object,
  onActionSuccess: PropTypes.func,
};

DefaultItemViewer.propTypes = {
  resource: PropTypes.object,
  onActionSuccess: PropTypes.func,
};

AddControl.propTypes = {
  childResourceType: PropTypes.arrayOf(PropTypes.string).isRequired,
  parentId: PropTypes.string.isRequired,
  parentType: PropTypes.string.isRequired,
  parentObject: PropTypes.object,
  creationConfig: PropTypes.object,
  onAddSuccess: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  existingReferences: PropTypes.array,
};

export default ReferenceManager;